import { Link } from "@tanstack/react-router";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import type { IntentName } from "@/design";
import { useIsAdmin } from "@/shared/auth";
import { MarketplaceChip } from "@/shared/components/MarketplaceChip";
import { toast } from "@/shared/lib/toast";

import { useRetryAwb } from "../api/use-retry-awb";
import type { StuckCauseKey, StuckGroup } from "../lib/stuck-cause";

const CAUSE_INTENT: Record<StuckCauseKey, IntentName> = {
  reconnect: "danger",
  retry: "warning",
  pending: "neutral",
  never_tried: "warning",
};

// One action per card, styled like KpiCard's link — a button beside a link
// was two competing CTAs. The order list itself is the table below.
const ACTION =
  "shrink-0 text-xs font-semibold text-[var(--brand-600)] hover:underline disabled:cursor-not-allowed disabled:opacity-50";

const COUNT_CLASS: Record<IntentName, string> = {
  success: "text-[var(--intent-success-fg)]",
  warning: "text-[var(--intent-warning-fg)]",
  danger: "text-[var(--intent-danger-fg)]",
  info: "text-[var(--intent-info-fg)]",
  neutral: "text-[var(--text-primary)]",
};

/**
 * One card per (cause × marketplace) bucket of stuck orders, each with the
 * single action that clears it. This is where ops decides; the table
 * underneath is for drilling into one order.
 */
export function StuckCauseCards({ groups }: { groups: StuckGroup[] }): JSX.Element | null {
  const { t } = useTranslation("dashboard");
  const isAdmin = useIsAdmin();
  const retryAwb = useRetryAwb();

  if (groups.length === 0) return null;

  // Fire one fetch-awb per order; `isPending` stays true while any is in
  // flight, and `useRetryAwb` already refetches the snapshot on success.
  const retryAll = (ids: string[]): void => {
    for (const id of ids) {
      retryAwb.mutate(id, { onError: (e) => toast.error(apiErrorMessage(e)) });
    }
  };

  return (
    <div className="grid grid-cols-1 gap-3 border-b border-[var(--premium-border)] p-4 sm:grid-cols-2 lg:grid-cols-3">
      {groups.map((g) => {
        const intent = CAUSE_INTENT[g.cause];
        return (
          <div
            key={`${g.cause}:${g.marketplace}`}
            className="flex items-center justify-between gap-4 rounded-[10px] border border-[var(--premium-border)] bg-[var(--surface-sunken)] px-4 py-3"
            data-testid="stuck-cause-card"
            data-cause={g.cause}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className={`text-2xl font-extrabold tabular-nums ${COUNT_CLASS[intent]}`}>
                {g.count}
              </span>
              <div className="flex min-w-0 flex-col items-start gap-1">
                <MarketplaceChip marketplace={g.marketplace} />
                <span className="truncate text-xs font-medium text-[var(--text-primary)]">
                  {t(`dashboard:stuck.cause.${g.cause}`)}
                </span>
              </div>
            </div>
            {g.cause === "reconnect" && (
              <Link to="/settings/marketplace-connections" className={ACTION}>
                {t("dashboard:pipeline.reconnect")}
              </Link>
            )}
            {g.cause === "retry" && isAdmin && (
              <button
                type="button"
                className={ACTION}
                disabled={retryAwb.isPending}
                onClick={() => retryAll(g.orderIds)}
                data-testid="stuck-retry-all"
              >
                {retryAwb.isPending
                  ? t("dashboard:stuck.retrying")
                  : t("dashboard:stuck.retry_all", { n: g.count })}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
